import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Plus, Check, X, FileText, Boxes } from "lucide-react";
import { toast } from "sonner";
import ImageUploader from "@/components/ImageUploader";
import ZoomableImage from "@/components/ZoomableImage";

const STATUS_STYLES = {
  pending: "bg-amber-600/20 text-amber-400 border-amber-600/40",
  approved: "bg-emerald-600/20 text-emerald-400 border-emerald-600/40",
  rejected: "bg-rose-600/20 text-rose-400 border-rose-600/40",
};
const STATUS_LABELS = { pending: "Beklemede", approved: "Onaylı", rejected: "Reddedildi" };

export default function OlayRaporu() {
  const [me, setMe] = useState(null);
  useEffect(() => { api.get("/memur/me").then(r => setMe(r.data)); }, []);
  const canApprove = me?.is_officer_manager; // yaklaşık istemci-taraflı gösterim; asıl kontrol sunucuda

  return (
    <div className="p-6 md:p-10 space-y-6 max-w-5xl">
      <header>
        <div className="text-[11px] font-mono text-rose-500 tracking-widest mb-2">// POLİS DEPARTMANI</div>
        <h1 className="font-heading text-4xl font-bold tracking-tight">Olay Raporu &amp; Kanıt Deposu</h1>
        <p className="text-zinc-500 mt-2">Olay raporları 60 gün sonra otomatik silinir.</p>
      </header>

      <Tabs defaultValue="raporlar">
        <TabsList className="bg-zinc-950 border border-zinc-900">
          <TabsTrigger value="raporlar" data-testid="section-raporlar"><FileText className="w-4 h-4 mr-2" />Olay Raporu</TabsTrigger>
          <TabsTrigger value="kanit" data-testid="section-kanit"><Boxes className="w-4 h-4 mr-2" />Kanıt Deposu</TabsTrigger>
        </TabsList>
        <TabsContent value="raporlar" className="mt-4"><OlayRaporuSection canApprove={canApprove} /></TabsContent>
        <TabsContent value="kanit" className="mt-4"><KanitDeposuSection canApprove={canApprove} /></TabsContent>
      </Tabs>
    </div>
  );
}

// ============ OLAY RAPORU BÖLÜMÜ ============
function OlayRaporuSection({ canApprove }) {
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ title: "", description: "", images: [] });
  const [reviewOpen, setReviewOpen] = useState(null);

  const load = () => api.get("/memur/olay-raporlari").then(r => setItems(r.data));
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.title.trim() || !form.description.trim()) return toast.error("Başlık ve açıklama gerekli");
    try {
      await api.post("/memur/olay-raporlari", form);
      toast.success("Olay raporu gönderildi");
      setOpen(false); setForm({ title: "", description: "", images: [] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
  };

  const review = async (status) => {
    try {
      await api.post(`/memur/olay-raporlari/${reviewOpen.id}/review`, { status });
      toast.success(status === "approved" ? "Onaylandı" : "Reddedildi");
      setReviewOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  const filtered = (status) => items.filter(i => status === "all" || i.status === status);

  const renderList = (list) => (
    <div className="space-y-3">
      {list.length === 0 && (
        <Card className="bg-zinc-950 border-zinc-900 p-10 text-center">
          <p className="text-zinc-500 text-sm">Kayıt bulunamadı.</p>
        </Card>
      )}
      {list.map(r => (
        <Card key={r.id} className="bg-zinc-950 border-zinc-900 p-5" data-testid={`olay-raporu-${r.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[10px] font-mono px-2 py-0.5 border ${STATUS_STYLES[r.status]}`}>{STATUS_LABELS[r.status]}</span>
                <span className="font-semibold">{r.title}</span>
                <span className="text-[10px] font-mono text-zinc-500">// {r.officer_name} · {new Date(r.created_at).toLocaleString("tr-TR")}</span>
              </div>
              <p className="text-zinc-300 whitespace-pre-wrap text-sm">{r.description}</p>
              {r.images?.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {r.images.map((u, i) => <ZoomableImage key={i} src={u} className="w-20 h-20 object-cover border border-zinc-800" />)}
                </div>
              )}
              <div className="text-[10px] font-mono text-zinc-600 mt-2">60 gün sonra otomatik silinir · {new Date(r.expires_at).toLocaleDateString("tr-TR")}</div>
            </div>
            {canApprove && r.status === "pending" && (
              <Button size="sm" onClick={() => setReviewOpen(r)} data-testid={`review-olay-raporu-${r.id}`} className="bg-rose-600 hover:bg-rose-700">İncele</Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-olay-raporu-button" className="bg-rose-600 hover:bg-rose-700"><Plus className="w-4 h-4 mr-2" />Yeni Olay Raporu</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800">
            <DialogHeader><DialogTitle className="font-heading">Yeni Olay Raporu</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs font-mono">BAŞLIK</Label>
                <Input value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="olay-raporu-title-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <div><Label className="text-xs font-mono">AÇIKLAMA</Label>
                <Textarea rows={4} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="olay-raporu-description-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <div><Label className="text-xs font-mono">RESİMLER</Label>
                <div className="mt-1.5"><ImageUploader value={form.images} onChange={(v) => setForm({ ...form, images: v })} /></div></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="olay-raporu-submit-button" className="bg-rose-600 hover:bg-rose-700">Gönder</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="bg-zinc-950 border border-zinc-900">
          <TabsTrigger value="pending">Bekleyen</TabsTrigger>
          <TabsTrigger value="approved">Onaylı</TabsTrigger>
          <TabsTrigger value="rejected">Red</TabsTrigger>
          <TabsTrigger value="all">Tümü</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-4">{renderList(filtered("pending"))}</TabsContent>
        <TabsContent value="approved" className="mt-4">{renderList(filtered("approved"))}</TabsContent>
        <TabsContent value="rejected" className="mt-4">{renderList(filtered("rejected"))}</TabsContent>
        <TabsContent value="all" className="mt-4">{renderList(filtered("all"))}</TabsContent>
      </Tabs>

      <Dialog open={!!reviewOpen} onOpenChange={v => !v && setReviewOpen(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader><DialogTitle className="font-heading">Raporu İncele</DialogTitle></DialogHeader>
          {reviewOpen && (
            <div className="space-y-3">
              <div className="font-semibold">{reviewOpen.title}</div>
              <p className="text-zinc-300 bg-zinc-900/60 p-3 border-l-2 border-rose-500/50 whitespace-pre-wrap text-sm">{reviewOpen.description}</p>
            </div>
          )}
          <DialogFooter className="gap-2">
            <Button onClick={() => review("rejected")} data-testid="reject-olay-raporu-button" variant="outline" className="border-rose-600/40 text-rose-400 hover:bg-rose-950/30"><X className="w-4 h-4 mr-1" />Reddet</Button>
            <Button onClick={() => review("approved")} data-testid="approve-olay-raporu-button" className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4 mr-1" />Onayla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ============ KANIT DEPOSU BÖLÜMÜ ============
function KanitDeposuSection({ canApprove }) {
  const [items, setItems] = useState([]);
  const [myReports, setMyReports] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ olay_raporu_id: "", items_description: "", images: [] });
  const [reviewOpen, setReviewOpen] = useState(null);

  const load = () => api.get("/memur/kanit-deposu").then(r => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/memur/olay-raporlari").then(r => setMyReports(r.data));
  }, []);

  const submit = async () => {
    if (!form.olay_raporu_id || !form.items_description.trim()) return toast.error("Rapor ve eşya açıklaması gerekli");
    try {
      await api.post("/memur/kanit-deposu", form);
      toast.success("Kanıt kaydı gönderildi");
      setOpen(false); setForm({ olay_raporu_id: "", items_description: "", images: [] }); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Gönderilemedi"); }
  };

  const review = async (status) => {
    try {
      await api.post(`/memur/kanit-deposu/${reviewOpen.id}/review`, { status });
      toast.success(status === "approved" ? "Onaylandı" : "Reddedildi");
      setReviewOpen(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "İşlem başarısız"); }
  };

  const filtered = (status) => items.filter(i => status === "all" || i.status === status);

  const renderList = (list) => (
    <div className="space-y-3">
      {list.length === 0 && (
        <Card className="bg-zinc-950 border-zinc-900 p-10 text-center">
          <p className="text-zinc-500 text-sm">Kayıt bulunamadı.</p>
        </Card>
      )}
      {list.map(k => (
        <Card key={k.id} className="bg-zinc-950 border-zinc-900 p-5" data-testid={`kanit-${k.id}`}>
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap mb-2">
                <span className={`text-[10px] font-mono px-2 py-0.5 border ${STATUS_STYLES[k.status]}`}>{STATUS_LABELS[k.status]}</span>
                <span className="text-[10px] font-mono text-zinc-500">// {k.officer_name} · {new Date(k.created_at).toLocaleString("tr-TR")}</span>
              </div>
              <p className="text-zinc-300 whitespace-pre-wrap text-sm">{k.items_description}</p>
              {k.images?.length > 0 && (
                <div className="flex gap-2 mt-3 flex-wrap">
                  {k.images.map((u, i) => <ZoomableImage key={i} src={u} className="w-20 h-20 object-cover border border-zinc-800" />)}
                </div>
              )}
            </div>
            {canApprove && k.status === "pending" && (
              <Button size="sm" onClick={() => setReviewOpen(k)} data-testid={`review-kanit-${k.id}`} className="bg-rose-600 hover:bg-rose-700">İncele</Button>
            )}
          </div>
        </Card>
      ))}
    </div>
  );

  return (
    <div className="space-y-4">
      <div className="flex justify-end">
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button data-testid="new-kanit-button" className="bg-rose-600 hover:bg-rose-700"><Plus className="w-4 h-4 mr-2" />Kanıt Ekle</Button>
          </DialogTrigger>
          <DialogContent className="bg-zinc-950 border-zinc-800">
            <DialogHeader><DialogTitle className="font-heading">Kanıt Deposuna Ekle</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div><Label className="text-xs font-mono">İLGİLİ OLAY RAPORU</Label>
                <Select value={form.olay_raporu_id} onValueChange={v => setForm({ ...form, olay_raporu_id: v })}>
                  <SelectTrigger data-testid="kanit-report-select" className="bg-zinc-900 border-zinc-800 mt-1.5"><SelectValue placeholder="Kendi raporunuzu seçin" /></SelectTrigger>
                  <SelectContent className="bg-zinc-950 border-zinc-800">
                    {myReports.map(r => <SelectItem key={r.id} value={r.id}>{r.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div><Label className="text-xs font-mono">EL KONULAN EŞYALAR</Label>
                <Textarea rows={4} value={form.items_description} onChange={e => setForm({ ...form, items_description: e.target.value })} data-testid="kanit-items-input" className="bg-zinc-900 border-zinc-800 mt-1.5" /></div>
              <div><Label className="text-xs font-mono">RESİMLER (opsiyonel)</Label>
                <div className="mt-1.5"><ImageUploader value={form.images} onChange={(v) => setForm({ ...form, images: v })} /></div></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="kanit-submit-button" className="bg-rose-600 hover:bg-rose-700">Gönder</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      <Tabs defaultValue="pending">
        <TabsList className="bg-zinc-950 border border-zinc-900">
          <TabsTrigger value="pending">Bekleyen</TabsTrigger>
          <TabsTrigger value="approved">Onaylı</TabsTrigger>
          <TabsTrigger value="rejected">Red</TabsTrigger>
          <TabsTrigger value="all">Tümü</TabsTrigger>
        </TabsList>
        <TabsContent value="pending" className="mt-4">{renderList(filtered("pending"))}</TabsContent>
        <TabsContent value="approved" className="mt-4">{renderList(filtered("approved"))}</TabsContent>
        <TabsContent value="rejected" className="mt-4">{renderList(filtered("rejected"))}</TabsContent>
        <TabsContent value="all" className="mt-4">{renderList(filtered("all"))}</TabsContent>
      </Tabs>

      <Dialog open={!!reviewOpen} onOpenChange={v => !v && setReviewOpen(null)}>
        <DialogContent className="bg-zinc-950 border-zinc-800">
          <DialogHeader><DialogTitle className="font-heading">Kanıtı İncele</DialogTitle></DialogHeader>
          {reviewOpen && <p className="text-zinc-300 bg-zinc-900/60 p-3 border-l-2 border-rose-500/50 whitespace-pre-wrap text-sm">{reviewOpen.items_description}</p>}
          <DialogFooter className="gap-2">
            <Button onClick={() => review("rejected")} data-testid="reject-kanit-button" variant="outline" className="border-rose-600/40 text-rose-400 hover:bg-rose-950/30"><X className="w-4 h-4 mr-1" />Reddet</Button>
            <Button onClick={() => review("approved")} data-testid="approve-kanit-button" className="bg-emerald-600 hover:bg-emerald-700"><Check className="w-4 h-4 mr-1" />Onayla</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
